import React, { useState } from 'react';

const CheckoutComponent = ({ 
  selectedRooms, 
  selectedAddOns, 
  selectedDates, 
  guests, 
  total, 
  nights, 
  onBackToSummary,
  onCheckoutComplete 
}) => {
  const [guestInfo, setGuestInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: 'UAE',
    postalCode: ''
  });
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);

  const validateForm = () => {
    // Validate guest info
    const requiredGuest = ['firstName', 'lastName', 'email', 'phone'];
    for (const field of requiredGuest) {
      if (!guestInfo[field].trim()) {
        alert(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestInfo.email)) {
      alert('Please enter a valid email address');
      return false;
    }
    
    // Basic phone validation (at least 10 digits)
    const phoneDigits = guestInfo.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      alert('Please enter a valid phone number (at least 10 digits)');
      return false;
    }

    // Validate payment info
    const requiredPayment = ['cardNumber', 'expiryDate', 'cvv', 'cardholderName'];
    for (const field of requiredPayment) {
      if (!paymentInfo[field].trim()) {
        alert(`Please fill in ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        return false;
      }
    }
    
    // Basic card validation
    if (paymentInfo.cardNumber.replace(/\s/g, '').length < 13) {
      alert('Please enter a valid card number');
      return false;
    }
    
    if (paymentInfo.cvv.length < 3) {
      alert('Please enter a valid CVV');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsProcessing(true);
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate booking confirmation
      const confirmation = {
        bookingId: 'BK' + Date.now().toString().slice(-8),
        bookingDate: new Date().toISOString(),
        checkIn: selectedDates.checkIn,
        checkOut: selectedDates.checkOut,
        nights: nights,
        totalAmount: total,
        guestInfo,
        selectedRooms,
        selectedAddOns
      };
      
      setBookingConfirmation(confirmation);
      
      // Notify parent component
      if (onCheckoutComplete) {
        onCheckoutComplete(confirmation);
      }
    } catch (error) {
      alert('Payment processing failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  if (bookingConfirmation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 mb-6 shadow-lg">
              <svg className="h-10 w-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">🎉 Booking Confirmed!</h1>
            <p className="text-xl text-gray-600">Your reservation has been successfully confirmed</p>
          </div>

          {/* Confirmation Details */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8 border border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <svg className="h-6 w-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Booking Details
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                    <span className="text-gray-600 font-medium">Booking ID:</span>
                    <span className="font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full text-sm">{bookingConfirmation.bookingId}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                    <span className="text-gray-600 font-medium">Check-in:</span>
                    <span className="font-semibold text-gray-900">{new Date(bookingConfirmation.checkIn).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                    <span className="text-gray-600 font-medium">Check-out:</span>
                    <span className="font-semibold text-gray-900">{new Date(bookingConfirmation.checkOut).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                    <span className="text-gray-600 font-medium">Nights:</span>
                    <span className="font-semibold text-gray-900">{bookingConfirmation.nights}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-white rounded-lg shadow-sm">
                    <span className="text-gray-600 font-medium">Total Amount:</span>
                    <span className="text-2xl font-bold text-green-600">₹{bookingConfirmation.totalAmount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <svg className="h-6 w-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Guest Information
                </h3>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
                    <p className="text-gray-900 text-lg font-semibold mb-2">
                      {bookingConfirmation.guestInfo.firstName} {bookingConfirmation.guestInfo.lastName}
                    </p>
                    <p className="text-gray-600 flex items-center">
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      {bookingConfirmation.guestInfo.email}
                    </p>
                    <p className="text-gray-600 flex items-center mt-2">
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      {bookingConfirmation.guestInfo.phone}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={() => window.print()}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>Print Confirmation</span>
            </button>
            <button
              onClick={() => {
                setBookingConfirmation(null);
              }}
              className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-4 rounded-xl font-semibold hover:from-gray-700 hover:to-gray-800 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>New Booking</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Single Step Checkout Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">💳 Complete Checkout</h1>
          <p className="text-xl text-gray-600">Fill in your details and payment information to complete your booking</p>
        </div>

        {/* Single Step Checkout Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-10 border border-gray-100">
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-16">
            {/* Left Column - Combined Form */}
            <div className="xl:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Guest Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <svg className="h-7 w-7 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Guest Information
                  </h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-base font-semibold text-gray-700 mb-3">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={guestInfo.firstName}
                        onChange={(e) => setGuestInfo({...guestInfo, firstName: e.target.value})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                        placeholder="John"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-base font-semibold text-gray-700 mb-3">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={guestInfo.lastName}
                        onChange={(e) => setGuestInfo({...guestInfo, lastName: e.target.value})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label htmlFor="email" className="block text-base font-semibold text-gray-700 mb-3">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={guestInfo.email}
                        onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                        placeholder="john.doe@example.com"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-base font-semibold text-gray-700 mb-3">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={guestInfo.phone}
                        onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                        placeholder="+971 50 123 4567"
                        required
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="address" className="block text-base font-semibold text-gray-700 mb-3">
                      Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      value={guestInfo.address}
                      onChange={(e) => setGuestInfo({...guestInfo, address: e.target.value})}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                      placeholder="Street address"
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                    <div>
                      <label htmlFor="city" className="block text-base font-semibold text-gray-700 mb-3">
                        City
                      </label>
                      <input
                        type="text"
                        id="city"
                        value={guestInfo.city}
                        onChange={(e) => setGuestInfo({...guestInfo, city: e.target.value})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                        placeholder="Abu Dhabi"
                      />
                    </div>
                    <div>
                      <label htmlFor="country" className="block text-base font-semibold text-gray-700 mb-3">
                        Country
                      </label>
                      <select
                        id="country"
                        value={guestInfo.country}
                        onChange={(e) => setGuestInfo({...guestInfo, country: e.target.value})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                      >
                        <option value="UAE">United Arab Emirates</option>
                        <option value="USA">United States</option>
                        <option value="UK">United Kingdom</option>
                        <option value="CA">Canada</option>
                        <option value="AU">Australia</option>
                        <option value="IN">India</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="postalCode" className="block text-base font-semibold text-gray-700 mb-3">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        id="postalCode"
                        value={guestInfo.postalCode}
                        onChange={(e) => setGuestInfo({...guestInfo, postalCode: e.target.value})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 text-lg transition-all duration-200"
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Information Section */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <svg className="h-7 w-7 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Payment Information
                  </h3>
                  
                  <div>
                    <label htmlFor="cardholderName" className="block text-base font-semibold text-gray-700 mb-3">
                      Cardholder Name *
                    </label>
                    <input
                      type="text"
                      id="cardholderName"
                      value={paymentInfo.cardholderName}
                      onChange={(e) => setPaymentInfo({...paymentInfo, cardholderName: e.target.value})}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 text-lg transition-all duration-200"
                      placeholder="Name on card"
                      required
                    />
                  </div>

                  <div className="mt-6">
                    <label htmlFor="cardNumber" className="block text-base font-semibold text-gray-700 mb-3">
                      Card Number *
                    </label>
                    <input
                      type="text"
                      id="cardNumber"
                      value={paymentInfo.cardNumber}
                      onChange={(e) => setPaymentInfo({...paymentInfo, cardNumber: formatCardNumber(e.target.value)})}
                      className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 text-lg transition-all duration-200"
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6 mt-6">
                    <div>
                      <label htmlFor="expiryDate" className="block text-base font-semibold text-gray-700 mb-3">
                        Expiry Date *
                      </label>
                      <input
                        type="text"
                        id="expiryDate"
                        value={paymentInfo.expiryDate}
                        onChange={(e) => setPaymentInfo({...paymentInfo, expiryDate: formatExpiryDate(e.target.value)})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 text-lg transition-all duration-200"
                        placeholder="MM/YY"
                        maxLength="5"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="cvv" className="block text-base font-semibold text-gray-700 mb-3">
                        CVV *
                      </label>
                      <input
                        type="text"
                        id="cvv"
                        value={paymentInfo.cvv}
                        onChange={(e) => setPaymentInfo({...paymentInfo, cvv: e.target.value.replace(/\D/g, '')})}
                        className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500 text-lg transition-all duration-200"
                        placeholder="123"
                        maxLength="4"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-8 pt-8">
                  <button
                    type="button"
                    onClick={onBackToSummary}
                    className="flex-1 px-8 py-5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 text-lg font-semibold hover:border-gray-400 transform hover:scale-105"
                  >
                    ← Back to Summary
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-5 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-lg transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        <span>Processing Payment...</span>
                      </div>
                    ) : (
                      'Complete Booking →'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column - Booking Summary */}
            <div className="xl:col-span-2">
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 sticky top-6 border-2 border-blue-100 shadow-xl">
                <h4 className="font-bold text-gray-900 mb-8 text-2xl flex items-center">
                  <svg className="h-7 w-7 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Booking Summary
                </h4>
                <div className="space-y-6">
                  <div className="flex justify-between text-lg p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <span className="text-gray-600 font-medium">Check-in:</span>
                    <span className="font-semibold text-gray-900">{new Date(selectedDates.checkIn).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between text-lg p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <span className="text-gray-600 font-medium">Check-out:</span>
                    <span className="font-semibold text-gray-900">{new Date(selectedDates.checkOut).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between text-lg p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <span className="text-gray-600 font-medium">Nights:</span>
                    <span className="font-semibold text-gray-900">{nights}</span>
                  </div>
                  <div className="flex justify-between text-lg p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                    <span className="text-gray-600 font-medium">Guests:</span>
                    <span className="font-semibold text-gray-900">{guests.adults + guests.children}</span>
                  </div>
                  
                  <hr className="my-8 border-gray-200" />
                  
                  {/* Selected Rooms */}
                  {selectedRooms.length > 0 && (
                    <div>
                      <h5 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                        <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Selected Rooms:
                      </h5>
                      <div className="space-y-4">
                        {selectedRooms.map((item, index) => (
                          <div key={index} className="text-base text-gray-600 bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="font-semibold text-gray-800 text-lg mb-2">{item.room.name}</div>
                            <div className="text-gray-500 mb-3 bg-gray-50 px-3 py-1 rounded-lg text-sm">{item.ratePlan.name}</div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 font-medium">Qty: {item.quantity}</span>
                              <span className="font-bold text-lg text-blue-600">₹{((item.room.basePrice * (1 + item.ratePlan.priceModifier)) * item.quantity * nights).toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Selected Add-ons */}
                  {selectedAddOns.length > 0 && (
                    <div className="mt-6">
                      <h5 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                        <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Selected Add-ons:
                      </h5>
                      <div className="space-y-4">
                        {selectedAddOns.map((item, index) => (
                          <div key={index} className="text-base text-gray-600 bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <div className="font-semibold text-gray-800 text-lg mb-2">{item.addOn.name}</div>
                            <div className="flex justify-between items-center">
                              <span className="text-gray-600 font-medium">Qty: {item.quantity}</span>
                              <span className="font-bold text-lg text-green-600">₹{((item.option?.price || item.addOn.price) * item.quantity).toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <hr className="my-8 border-gray-200" />
                  
                  {/* Total */}
                  <div className="text-center bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-2xl border-2 border-green-200 shadow-lg">
                    <div className="text-5xl font-bold text-green-600 mb-4">₹{total.toLocaleString()}</div>
                    <div className="text-xl text-gray-600 font-medium">Total for {nights} nights</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutComponent;
